import { useEffect, useState } from "react";
import { Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ACTIONS } from "@/lib/actions";
import { publicApi, errMsg } from "@/lib/api";
import { useVisitor } from "@/lib/visitor";
import { useContent } from "@/lib/content-store";
import { scheduleLabel } from "@/lib/schedule";

const DetailField = ({ field, value, onChange, content }) => {
  const options = field.type === "service" ? (content.schedules || []).map(scheduleLabel)
    : field.type === "ministry" ? content.ministries : field.options;
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">{field.label}</Label>
      {options ? (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger data-testid={`action-field-${field.key}`}><SelectValue placeholder="Choose one" /></SelectTrigger>
          <SelectContent className="bg-white">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={`action-field-${field.key}`} />
      )}
    </div>
  );
};

const ThankYou = ({ name, onClose }) => (
  <div className="py-6 text-center" data-testid="action-form-success">
    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-[#EBF3FA] text-[#1D64B4]"><HeartHandshake className="h-7 w-7" /></span>
    <h3 className="mt-4 text-xl font-semibold text-slate-900">Thank you, {name}!</h3>
    <p className="mt-2 text-sm text-slate-600">Someone from our team will gladly reach out to you soon. God bless you!</p>
    <Button onClick={onClose} className="mt-6 rounded-full bg-[#0A3A6B] px-8 hover:bg-[#1D64B4]" data-testid="action-form-close">Close</Button>
  </div>
);

export const ActionForm = ({ state, onClose }) => {
  const { profile, setProfile } = useVisitor();
  const content = useContent();
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [details, setDetails] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const cfg = state && ACTIONS[state.id];

  useEffect(() => {
    if (state) {
      setForm({ name: profile?.nickname || "", phone: profile?.mobile || "", email: profile?.email || "", message: "" });
      setDetails(state.preset || {});
      setDone(false);
    }
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps -- only reset when a new action opens (profile updates on submit)

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.phone.trim().length < 7) return toast.error("Please share your nickname and mobile number so we can reach you.");
    setBusy(true);
    try {
      await publicApi.post("/requests", { action: state.id, visitor_id: profile?.id || "", ...form, details });
      setProfile({ ...(profile || {}), id: profile?.id || "", nickname: form.name.trim(), mobile: form.phone.trim(), email: form.email.trim() });
      setDone(true);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!cfg} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl bg-white sm:max-w-lg" data-testid="action-form-dialog">
        <DialogTitle className="font-display text-2xl font-bold tracking-tight text-[#0A3A6B]">{cfg?.title}</DialogTitle>
        <DialogDescription className="text-slate-600">{cfg?.intro}</DialogDescription>
        {done ? <ThankYou name={form.name} onClose={onClose} /> : cfg && (
          <form onSubmit={submit} className="space-y-4" data-testid={`action-form-${state.id}`}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Nickname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="action-nickname-input" />
              <Input type="tel" inputMode="tel" placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="action-mobile-input" />
            </div>
            <Input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="action-email-input" />
            {cfg.fields.map((f) => (
              <DetailField key={f.key} field={f} content={content} value={details[f.key]} onChange={(v) => setDetails({ ...details, [f.key]: v })} />
            ))}
            <Textarea rows={3} placeholder="Anything else you'd like to share with us?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="action-message-input" />
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="action-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
